import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Box, IconButton, makeStyles, Typography } from '@material-ui/core';

import { formatPrice } from 'common/utils/format';
import { CartUnitTypes, IInvoiceDetailsImt } from 'common/interfaces/invoices';
import { CustomTheme } from 'common/ui/interfaces';

import InvoiceServiceItem from 'common/components/InvoiceOperating/InvoiceServiceItem/InvoiceServiceItem';
import InvoiceTotals from 'common/components/InvoiceOperating/InvoiceTotals/InvoiceTotals';
import InvoiceTopUpBalanceItem from 'common/components/InvoiceOperating/InvoiceTopUpBalanceItem/InvoiceTopUpBalanceItem';
import InvoicePastDueItem from 'common/components/InvoiceOperating/InvoicePastDueItem/InvoicePastDueItem';
import InvoiceBillingItem from 'common/components/InvoiceOperating/InvoiceBillingItem/InvoiceBillingItem';
import { ReactComponent as InfoIcon } from 'img/icons/info.svg';
import InvoiceGiftCardItem from '../../InvoiceGiftCardItem/InvoiceGiftCardItem';
import invoiceOperatingMessages from 'common/components/InvoiceOperating/messages';
import { transformInvoiceBundleDTO } from '../../utils';

const useStyles = makeStyles((theme: CustomTheme) => ({
  itemsContainer: {
    borderTop: `1px solid ${theme.palette.borderColor?.main}`,
  },
  productItem: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.borderColor?.main}`,
    },
  },
  topUpBalanceItem: {
    borderBottom: `1px solid ${theme.palette.borderColor?.main}`,
  },
  itemPrice: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
  },
  priceHint: {
    display: 'inline-block',
    color: theme.palette.secondary.dark,
    fontWeight: 400,
  },
  infoIcon: {
    width: '1rem',
    height: '1rem',
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

interface IProps {
  checkOutData: IInvoiceDetailsImt;
  onEditTotal?: () => void;
  onViewInventoryInfo?: (inventoryId: string) => void;
  onViewPackageInfo?: (packageId: string) => void;
}

const CheckOutResponsePaymentInfo = ({
  checkOutData,
  onEditTotal,
  onViewInventoryInfo,
  onViewPackageInfo,
}: IProps): JSX.Element => {
  const intl = useIntl();

  const transformedCheckOutData = useMemo(() => checkOutData.toJS(), [checkOutData]);

  const {
    invoicePaymentSplits,
    totalAmount,
    subtotalAmount,
    discount,
    taxAmount,
    tipsAmount,
    invoiceUnits,
    discountAmount,
  } = transformedCheckOutData;

  const classes = useStyles();

  return (
    <Box className={classes.itemsContainer}>
      {invoiceUnits?.map(invoiceUnit => {
        const { id, type, subtotalAmount: unitSubtotalAmount } = invoiceUnit;

        switch (type) {
          case CartUnitTypes.INVENTORY: {
            const { inventory, subtotalDiscountAmount } = invoiceUnit;

            return (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                className={classes.productItem}
                key={id}
                mt={1}
              >
                <Box mb={1}>
                  <Box display="flex" alignItems="center" gridGap={4}>
                    <Typography variant="h5" component="p">
                      {`${inventory.number} x ${inventory.title}`}
                    </Typography>

                    {!!onViewInventoryInfo && (
                      <IconButton
                        size="small"
                        type="button"
                        color="primary"
                        onClick={() => onViewInventoryInfo(inventory.id || '')}
                      >
                        <InfoIcon className={classes.infoIcon} />
                      </IconButton>
                    )}
                  </Box>
                  {!!subtotalDiscountAmount && (
                    <Typography component="span" className={classes.priceHint}>
                      {intl.formatMessage(invoiceOperatingMessages.discount)}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.itemPrice}>
                  <Box display="flex" alignItems="center">
                    {Number(inventory.number) > 1 && (
                      <Box mr={1}>
                        <Typography
                          component="span"
                          className={classes.priceHint}
                          variant="h5"
                          color="secondary"
                        >
                          {`${inventory.number} x ${formatPrice(inventory.price || 0)}`}
                        </Typography>
                      </Box>
                    )}
                    <Typography component="span" variant="h5">
                      {formatPrice(unitSubtotalAmount)}
                    </Typography>
                  </Box>

                  {!!subtotalDiscountAmount && (
                    <Typography component="span" className={classes.priceHint}>
                      {`-${formatPrice(subtotalDiscountAmount)}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }
          case CartUnitTypes.CANCEL_MEMBERSHIP:
          case CartUnitTypes.CANCEL_NON_MEMBERSHIP: {
            const { bundle, cancelPastDues, cancelBillingSchedules } = invoiceUnit;

            return (
              <InvoiceServiceItem
                key={bundle.id}
                invoicePackage={transformInvoiceBundleDTO(invoiceUnit)}
                pastDues={cancelPastDues}
                billingSchedules={cancelBillingSchedules}
                onViewDetailedPackageInfo={onViewPackageInfo}
              />
            );
          }
          case CartUnitTypes.BILLING_SCHEDULE: {
            const { billingSchedule } = invoiceUnit;

            return (
              <InvoiceBillingItem
                billing={billingSchedule}
                key={id}
                subtotalAmount={unitSubtotalAmount}
              />
            );
          }
          case CartUnitTypes.PAST_DUE: {
            const { pastDue, pastDueResolve } = invoiceUnit;

            return (
              <InvoicePastDueItem
                pastDue={pastDue}
                pastDueResolve={pastDueResolve}
                key={id}
                subtotalAmount={unitSubtotalAmount}
              />
            );
          }
          case CartUnitTypes.TOP_UP_BALANCE: {
            return (
              <Box className={classes.topUpBalanceItem}>
                <InvoiceTopUpBalanceItem disabled subtotalAmount={unitSubtotalAmount} />
              </Box>
            );
          }
          case CartUnitTypes.GIFT_CARD: {
            const { giftCard } = invoiceUnit;

            return (
              <InvoiceGiftCardItem
                giftCard={giftCard}
                disabled
                subtotalAmount={unitSubtotalAmount}
              />
            );
          }
          case CartUnitTypes.MEMBERSHIP_BUNDLE:
          case CartUnitTypes.SERVICE_BUNDLE: {
            return (
              <InvoiceServiceItem
                key={id}
                invoicePackage={transformInvoiceBundleDTO(invoiceUnit)}
                onViewDetailedPackageInfo={onViewPackageInfo}
              />
            );
          }
          case CartUnitTypes.REACTIVATE_MEMBERSHIP:
          case CartUnitTypes.REACTIVATE_NON_MEMBERSHIP: {
            const { reactivatePastDues } = invoiceUnit;

            return (
              <InvoiceServiceItem
                key={id}
                invoicePackage={transformInvoiceBundleDTO(invoiceUnit)}
                pastDues={reactivatePastDues}
                onViewDetailedPackageInfo={onViewPackageInfo}
              />
            );
          }
          default:
            return null;
        }
      })}

      <Box marginY={2}>
        <InvoiceTotals
          payments={invoicePaymentSplits}
          total={totalAmount}
          subtotal={subtotalAmount}
          tax={taxAmount}
          discount={discount}
          isCheckOutResponse
          onEditTotal={onEditTotal}
          discountAmount={discountAmount}
          tips={tipsAmount}
        />
      </Box>
    </Box>
  );
};

export default CheckOutResponsePaymentInfo;
